class FjProductForm extends HTMLElement {
  constructor() {
    super();
    this.form = this.querySelector('form');
    this.variantIdInput = this.querySelector('[data-variant-id]');
    this.quantityInput = this.querySelector('[data-quantity-input]');
    this.decreaseButton = this.querySelector('[data-quantity-decrease]');
    this.submitButton = this.querySelector('[data-add-to-cart]');
    this.stickySubmitButton = document.querySelector('[data-sticky-add-to-cart]');
    this.priceEl = document.querySelector('[data-product-price]');
    this.stockBadge = document.querySelector('[data-stock-badge]');
    this.cartAddUrl = `${this.dataset.cartAddUrl || '/cart/add'}.js`;
    this.errorEls = document.querySelectorAll('[data-form-error], [data-sticky-form-error]');

    this.variants = this.parseVariants();
    // One selected value per option index, seeded from the variant
    // Liquid rendered as selected, so every option starts in sync.
    const currentVariant = this.variants.find(
      (variant) => String(variant.id) === this.variantIdInput?.value
    );
    this.selectedOptions = currentVariant ? [...currentVariant.options] : [];

    this.form?.addEventListener('submit', this.onSubmit.bind(this));
    this.addEventListener('click', this.onClick.bind(this));
    this.quantityInput?.addEventListener('input', this.onQuantityInput.bind(this));
    this.syncDecreaseButtonState();
  }

  parseVariants() {
    const script = document.querySelector('[data-product-variants]');
    if (!script) return [];
    try {
      return JSON.parse(script.textContent);
    } catch (e) {
      console.error('Could not parse product variants JSON:', e);
      return [];
    }
  }

  onClick(event) {
    const optionBtn = event.target.closest('[data-size-option]');
    if (optionBtn) {
      this.selectOption(Number(optionBtn.dataset.optionIndex), optionBtn.dataset.value);
      return;
    }
    if (event.target.closest('[data-quantity-increase]')) {
      this.adjustQuantity(1);
      return;
    }
    if (event.target.closest('[data-quantity-decrease]')) {
      this.adjustQuantity(-1);
    }
  }

  selectOption(optionIndex, value) {
    this.hideError();
    this.selectedOptions[optionIndex] = value;

    // Only toggle buttons in the same option group, so picking a value in
    // one option doesn't deselect another option's buttons.
    const syncGroup = (btn) => {
      if (Number(btn.dataset.optionIndex) !== optionIndex) return;
      btn.classList.toggle('is-selected', btn.dataset.value === value);
    };
    this.querySelectorAll('[data-size-option]').forEach(syncGroup);
    document.querySelectorAll('[data-sticky-size-option]').forEach(syncGroup);

    const matchedVariant = this.variants.find((variant) =>
      variant.options.every((option, index) => option === this.selectedOptions[index])
    );

    // No variant exists for this combination (only possible with 2+ options).
    if (!matchedVariant) {
      if (this.stockBadge) {
        this.stockBadge.textContent = 'Unavailable';
        this.stockBadge.classList.remove('fj-badge--in-stock');
        this.stockBadge.classList.add('fj-badge--out-of-stock');
      }
      this.setSubmitDisabled(true);
      return;
    }

    this.variantIdInput.value = matchedVariant.id;

    if (this.priceEl) {
      this.priceEl.textContent = matchedVariant.price;
    }
    if (this.stockBadge) {
      this.stockBadge.textContent = matchedVariant.available ? 'In Stock' : 'Out of Stock';
      this.stockBadge.classList.toggle('fj-badge--in-stock', matchedVariant.available);
      this.stockBadge.classList.toggle('fj-badge--out-of-stock', !matchedVariant.available);
    }
    if (this.submitButton) {
      this.setSubmitDisabled(!matchedVariant.available);
    }
  }

  onQuantityInput() {
    const value = Math.max(1, Number(this.quantityInput.value) || 1);
    this.quantityInput.value = value;
    const stickyInput = document.querySelector('[data-sticky-quantity-input]');
    if (stickyInput) stickyInput.value = value;
    this.syncDecreaseButtonState();
  }

  adjustQuantity(delta) {
    const current = Math.max(1, Number(this.quantityInput.value) || 1);
    const next = Math.max(1, current + delta);
    this.quantityInput.value = next;
    const stickyInput = document.querySelector('[data-sticky-quantity-input]');
    if (stickyInput) stickyInput.value = next;
    this.syncDecreaseButtonState();
  }

  syncDecreaseButtonState() {
    if (!this.quantityInput) return;
    const atMinimum = Number(this.quantityInput.value) <= 1;
    if (this.decreaseButton) this.decreaseButton.disabled = atMinimum;
    const stickyDecreaseButton = document.querySelector('[data-sticky-quantity-decrease]');
    if (stickyDecreaseButton) stickyDecreaseButton.disabled = atMinimum;
  }

  async onSubmit(event) {
    event.preventDefault();
    if (!this.submitButton || this.submitButton.disabled) return;

    this.hideError();

    const formData = new FormData(this.form);

    this.setSubmitDisabled(true);

    try {
      const allowed = await this.runBeforeAddChecks(formData);
      if (!allowed) return;
      
      const response = await fetch(this.cartAddUrl, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: formData,
      });
      const data = await response.json();

      // Shopify returns 422 with a readable `description` for things like
      // "only 2 left in stock" — show that to the shopper as-is.
      if (!response.ok) {
        this.showError(data.description || data.message);
        return;
      }

      this.dispatchEvent(
        new CustomEvent('fj:cart:added', {
          bubbles: true,
          detail: { itemAdded: data },
        })
      );
    } catch (e) {
      console.error('Failed to add to cart:', e);
      this.showError();
    } finally {
      this.setSubmitDisabled(false);
    }
  }

  // Lets other modules (e.g. fj-skulabs-check.js) allow or block an add.
  async runBeforeAddChecks(formData) {
    const variantId = Number(formData.get('id'));
    const variant = this.variants.find((item) => item.id === variantId);
    const checks = [];

    this.dispatchEvent(
      new CustomEvent('fj:cart:before-add', {
        bubbles: true,
        detail: {
          sku: variant?.sku || null,
          quantity: Number(formData.get('quantity')) || 1,
          productTitle: this.dataset.productTitle,
          addCheck: (promise) => checks.push(promise),
        },
      })
    );

    const results = await Promise.all(checks);
    const blocked = results.find((result) => result?.allow === false);
    if (blocked) {
      this.showError(blocked.message);
      return false;
    }
    return true;
  }

  showError(message) {
    const text = message || 'Something went wrong adding this to your cart. Please try again.';
    this.errorEls.forEach((el) => {
      el.textContent = text;
      el.hidden = false;
    });
  }

  hideError() {
    this.errorEls.forEach((el) => {
      el.textContent = '';
      el.hidden = true;
    });
  }

  setSubmitDisabled(disabled) {
    if (this.submitButton) this.submitButton.disabled = disabled;
    if (this.stickySubmitButton) this.stickySubmitButton.disabled = disabled;
  }
}

customElements.define('fj-product-form', FjProductForm);

/**
 * Sticky add-to-cart bar — shows once the main form scrolls out of view.
 * See the "assumption" note in fj-product-main.liquid: currently mobile-only
 * (hidden on desktop via CSS), toggle that rule if this should also appear
 * on desktop.
 */
(function initStickyCart() {
  const stickyCart = document.querySelector('[data-sticky-cart]');
  const mainForm = document.getElementById('fj-product-form');
  if (!stickyCart || !mainForm) return;

  const observer = new IntersectionObserver(
    ([entry]) => {
      const scrolledPast = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      // stickyCart.hidden = !scrolledPast;
      stickyCart.classList.toggle('is-visible', scrolledPast);
    },
    { threshold: 0 }
  );
  observer.observe(mainForm);

  stickyCart.addEventListener('click', (event) => {
    if (event.target.closest('[data-sticky-add-to-cart]')) {
      mainForm.requestSubmit();
    }
    if (event.target.closest('[data-sticky-quantity-increase]')) {
      document.querySelector('[data-quantity-increase]')?.click();
    }
    if (event.target.closest('[data-sticky-quantity-decrease]')) {
      document.querySelector('[data-quantity-decrease]')?.click();
    }
    const optionBtn = event.target.closest('[data-sticky-size-option]');
    if (optionBtn) {
      const mainEquivalent = [...document.querySelectorAll('[data-size-option]')].find(
        (btn) =>
          btn.dataset.value === optionBtn.dataset.value &&
          btn.dataset.optionIndex === optionBtn.dataset.optionIndex
      );
      mainEquivalent?.click();
    }
  });
})();

/**
 * "Find Your Next Favorite" — fetched via Shopify's section rendering API
 * on page load, since the `recommendations` object is only populated when
 * accessed through this endpoint (see NOTE in fj-product-main.liquid).
 */
(function initRecommendations() {
  const container = document.querySelector('[data-recommendations-section]');
  if (!container) return;

  fetch(container.dataset.url)
    .then((response) => response.text())
    .then((html) => {
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const newContent = parsed.querySelector('.fj-product-recommendations');
      if (newContent) container.innerHTML = newContent.outerHTML;
    })
    .catch((e) => console.error('Failed to load recommendations:', e));
})();